import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { message, Popover, PopoverProps } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import useVoucherProduct from 'hooks/business/useVoucherProduct';
import { formatNumber } from 'utils/numberFormat';

export interface VoucherProductProps extends HTMLAttributes<HTMLDivElement> {
  voucher: Conversation.Voucher.Vo;
  product: Conversation.Voucher.Product;
}

export function VoucherProduct(props: VoucherProductProps) {
  const { voucher, product, ...otherProps } = props;

  const { unit } = voucher;

  const {
    itemName: name,
    firstImageUrl: imageUrl,
    originalPrice,
    skuLowPrice: lowPrice,
    skuHighPrice: highPrice,
  } = product;

  return (
    <Product {...otherProps}>
      <img src={imageUrl} alt="product" />
      <div>
        <h4 title={name}>{name}</h4>
        {lowPrice === highPrice ? (
          <p>{`${unit} ${formatNumber(lowPrice)}`}</p>
        ) : (
          <>
            <del>{`${unit} ${formatNumber(originalPrice)}`}</del>
            <p>{`${unit} ${formatNumber(lowPrice)} - ${unit} ${formatNumber(highPrice)}`}</p>
          </>
        )}
      </div>
    </Product>
  );
}

export interface VoucherProductPreviewerProps extends PopoverProps {
  storeId: Conversation.Conversation['storeId'];
  voucher: Conversation.Voucher.Vo;
}

export function VoucherProductPreviewer(props: VoucherProductPreviewerProps) {
  const { storeId, voucher, ...otherProps } = props;

  const { internalVoucherId: id, externalVoucherId } = voucher;

  const { t } = useTranslate();

  const [visible, setVisible] = useState(false);

  const { loading, list = [], fetchList } = useVoucherProduct();

  const refs = useRef({
    fetchList,
    loading,
    list,
  });
  refs.current = {
    fetchList,
    loading,
    list,
  };

  useEffect(() => {
    const {
      current: { fetchList, loading, list },
    } = refs;

    if (visible && !loading && list.length === 0) {
      fetchList({
        internal_voucher_id: id,
        internal_store_id: storeId,
        external_voucher_id: externalVoucherId,
      }).catch(() => {
        message.error(t('common.feedback.fetchFailed'));
      });
    }
  }, [visible, id, storeId, externalVoucherId, t]);

  return (
    <Popover
      arrowPointAtCenter
      visible={visible}
      onVisibleChange={setVisible}
      placement="left"
      autoAdjustOverflow={false}
      content={
        <ProductList>
          {loading ? (
            <LoadingOutlined />
          ) : (
            list.map((product) => {
              const { internalItemId: id } = product;

              return <VoucherProduct key={id} voucher={voucher} product={product} />;
            })
          )}
        </ProductList>
      }
      {...otherProps}
    />
  );
}

const Product = styled.div`
  display: flex;
  height: 56px;
  padding-top: 6px;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 2px;
  background: #fff;
  > img {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }
  > div {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > * {
      margin: 0;
      line-height: 10px;
      font-size: 10px;
    }
    > h4 {
      color: #515359;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    > del {
      color: #c9cacc;
    }
    > p {
      color: var(--fett-app-primary-color);
    }
  }
`;

const ProductList = styled.div`
  width: 285px;
  max-height: 320px;
  margin: -12px -16px;
  padding: 0;
  overflow: auto;

  > div {
    margin-bottom: 5px;
  }
`;
